import { Injectable, Injector, inject } from "@angular/core";
import { opsControlService } from "../ops-control/ops-control.service";
import type { LogEntry } from "./boardbook-model";

interface FuelContext {
  id: string;
  registration: string;
  applied?: number;
}

/** Only the latest timer flight may write back to the aircraft's live fuel. */
@Injectable({ providedIn: "root" })
export class TimerFuelService {
  private injector = inject(Injector);
  private context?: FuelContext;
  private readonly key = "efb.logbook.timerFuel";

  private read(): FuelContext | undefined {
    if (this.context) return this.context;
    try {
      return JSON.parse(sessionStorage.getItem(this.key) || "null") ?? undefined;
    } catch {
      return undefined;
    }
  }
  activate(id: string, registration: string): void {
    this.store({ id, registration });
  }
  clear(): void {
    this.context = undefined;
    try {
      sessionStorage.removeItem(this.key);
    } catch {
      /* Memory-only session. */
    }
  }
  private store(context: FuelContext): void {
    this.context = context;
    try {
      sessionStorage.setItem(this.key, JSON.stringify(context));
    } catch {
      /* Memory fallback. */
    }
  }
  isCurrent(id: string): boolean {
    return this.read()?.id === id;
  }

  /** Commits the live landing fuel at the timer stop. Never throws. */
  async applyLanding(id: string, landingFuelKg: number): Promise<void> {
    try {
      const context = this.read();
      if (!context || context.id !== id) return;
      if (!Number.isFinite(landingFuelKg) || landingFuelKg < 0) return;
      const ops = this.injector.get(opsControlService);
      if (ops.registration !== context.registration) return;
      const committed = await ops.fuelAndExtraMassPobSync(landingFuelKg);
      if (committed === undefined) return;
      if (this.read()?.id === id) this.store({ ...context, applied: landingFuelKg });
    } catch (error) {
      console.error("Landing fuel was not committed.", error);
    }
  }

  async apply(entry: LogEntry, unit: string): Promise<void> {
    const context = this.read();
    if (!context || context.id !== entry.id) return;
    if (unit !== "KG") throw new Error("Live fuel requires this timer entry's fuel unit to be kg.");
    if (entry.fuelAtLanding == null || !Number.isFinite(entry.fuelAtLanding) || entry.fuelAtLanding < 0)
      throw new Error("Enter a valid landing fuel before updating fuel on board.");
    const uplift = entry.fuelUplAfter ?? 0;
    if (!Number.isFinite(uplift) || uplift < 0) throw new Error("Enter a valid uplift amount.");
    const total = entry.fuelAtLanding + uplift;
    if (context.applied === total) return;
    const ops = this.injector.get(opsControlService);
    if (ops.registration !== context.registration) throw new Error("Aircraft changed; live fuel was not updated.");
    // Absolute total, like the old flight-record form: retries cannot add uplift twice.
    const committed = await ops.fuelAndExtraMassPobSync(total);
    if (committed === undefined)
      throw new Error("Flight saved, but fuel on board could not be updated. Save again to retry.");
    if (this.read()?.id === context.id) this.store({ ...context, applied: total });
  }
}
